import React, { useState } from 'react';
import { X, Crown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Dashboard from '../dashboard/Dashboard';
import IdeaAssessment from '../idea-validation/IdeaAssessment';
import BossMode from '../boss-mode/BossMode';
import ROITracker from '../roi-tracker/ROITracker';
import CommunityPods from '../community-pods/CommunityPods';
import FounderJournal from '../founder-journal/FounderJournal';
import Settings from '../settings/Settings';
import UsefulTools from '../useful-tools/UsefulTools';
import PlaybooksComponent from '../../pages/app/Playbooks';

interface DemoModeProps {
  onClose: () => void;
}

const DemoMode: React.FC<DemoModeProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', free: true, component: Dashboard },
    { id: 'idea-assessment', name: 'Idea Assessment', free: true, component: IdeaAssessment },
    { id: 'boss-mode', name: 'Boss Mode', free: false, component: BossMode },
    { id: 'roi-tracker', name: 'ROI Tracker', free: true, component: ROITracker },
    { id: 'community-pods', name: 'Community Pods', free: false, component: CommunityPods },
    { id: 'founder-journal', name: 'Founder Journal', free: false, component: FounderJournal },
    { id: 'playbooks', name: 'Playbooks', free: false, component: PlaybooksComponent },
    { id: 'useful-tools', name: 'Useful Tools', free: false, component: UsefulTools },
    { id: 'settings', name: 'Settings', free: false, component: Settings },
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabData?.component || Dashboard;

  const ProUpgradeOverlay = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-2xl">
        <Crown className="w-16 h-16 text-startup-gold mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-startup-navy mb-2">Go Pro for Full Access!</h3>
        <p className="text-startup-gray mb-6">
          Unlock {activeTabData?.name} and all premium features to supercharge your startup sprint.
        </p>
        <div className="space-y-3">
          <Link to="/signup">
            <Button className="w-full bg-gradient-startup text-white font-semibold py-3">
              Upgrade to Pro - $5.99/month
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={() => setActiveTab('dashboard')}
          >
            Back to Free Features
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-startup-slate z-40 overflow-hidden">
      {/* Demo Header */}
      <div className="bg-gradient-startup text-white p-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
            DEMO MODE
          </div>
          <h2 className="text-xl font-bold">StartupStreak Dashboard Preview</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Demo Tab Navigation */}
      <div className="bg-white border-b border-startup-gray/20 px-6 py-3">
        <div className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.free) {
                  setActiveTab(tab.id);
                }
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id && tab.free
                  ? 'bg-startup-teal text-white shadow-md'
                  : tab.free
                  ? 'text-startup-gray hover:text-startup-navy hover:bg-startup-slate'
                  : 'text-startup-gray/50 cursor-not-allowed'
              }`}
              disabled={!tab.free}
            >
              <span>{tab.name}</span>
              {!tab.free && (
                <Crown className="w-3 h-3 text-startup-gold" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Demo Content */}
      <div className="flex-1 overflow-hidden">
        {activeTabData?.free ? (
          <div className="h-full overflow-y-auto">
            <ActiveComponent />
          </div>
        ) : (
          <>
            <div className="h-full overflow-y-auto opacity-30 pointer-events-none">
              <ActiveComponent />
            </div>
            <ProUpgradeOverlay />
          </>
        )}
      </div>

      {/* Demo Footer CTA */}
      <div className="bg-white border-t border-startup-gray/20 p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <h3 className="font-semibold text-startup-navy">Ready to Start Your 30-Day Sprint?</h3>
            <p className="text-startup-gray text-sm">
              This is just a preview. Sign up to access all features and start building your business.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/signup">
              <Button className="bg-gradient-startup text-white font-semibold px-6">
                Start Your Sprint
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button variant="outline" onClick={onClose}>
              Close Demo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoMode;
